import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthLayout } from '../components/AuthLayout';
import { RegisterForm } from '../components/RegisterForm';
import { routePaths } from '@/app/routes';

/**
 * Account creation. Two steps: the form (account created directly on the auth
 * server) and a confirmation that sends the user to the hosted login. The
 * email verification code is mailed at that login, and `ProtectedRoute` then
 * routes the fresh session to the verify-email screen — so no OTP step here.
 */
type Step = 'form' | 'done';

export function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('form');

  return (
    <AuthLayout>
      <header className="mb-6 text-center">
        <h1 className="text-lg font-bold text-ink">{t('register.title')}</h1>
        {step === 'form' && <p className="mt-1 text-sm text-ink-mute">{t('register.subtitle')}</p>}
      </header>

      {step === 'form' && <RegisterForm onRegistered={() => setStep('done')} />}

      {step === 'done' && (
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-success-light text-success">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="font-semibold text-ink">{t('register.done_title')}</p>
          <p className="text-sm text-ink-mute">{t('register.done_hint')}</p>
          <button
            type="button"
            onClick={() => navigate(routePaths.login)}
            className="font-semibold text-primary hover:underline"
          >
            {t('register.go_login')}
          </button>
        </div>
      )}

      {step !== 'done' && (
        <p className="mt-7 text-center text-sm text-ink-soft">
          {t('register.have_account')}{' '}
          <Link to={routePaths.login} className="font-semibold text-primary hover:underline">
            {t('auth.login.submit')}
          </Link>
        </p>
      )}
    </AuthLayout>
  );
}
